/**
 * ReviewsTab Component
 * 
 * Unified employee reviews & sessions tab.
 * Combines performance review sessions and collaborative objective-setting sessions
 * into a single multi-view (list / monthly / calendar).
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import ConductReviewWorkspace from '@/components/performance/manager/ConductReviewWorkspace'
import RequestJITCheckinDialog from '@/components/performance/employee/RequestJITCheckinDialog'
import RescheduleSessionDialog from '@/components/performance/employee/RescheduleSessionDialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Award,
  Calendar,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Filter,
  Loader2,
  MapPin,
  PlusCircle,
  Users,
  Video
} from 'lucide-react'
import { reviewService } from '@/services/api/reviewService'
import { sessionManager } from '@/services/sessionManager'
import { useJourneyViewMode } from '@/lib/journeyViewMode'
import type { KPAData, EnhancedEvidenceEntry } from '@/types/evidence'
import { format } from 'date-fns'
import { sastDay, sastToday } from '@/components/admin/lifecycle/spellCoverage'
import { parseApiDate, sastCalendarDate } from '@/lib/apiDate'
import toast from 'react-hot-toast'

interface EmployeeContext {
  jobGroup: string
  reportingManager: string
  reportingManagerId?: string
  campus: string
  department: string
  performanceCycle: string
}

// Collaborative session interface (from CollaborationTab)
interface CollaborationSession {
  id: string
  employeeId: string
  employeeName: string
  employeeRole: string
  managerId: string
  managerName: string
  sessionType?: string
  status: 'scheduled' | 'pending-scheduling' | 'in-progress' | 'completed' | 'cancelled'
  meetingDetails?: {
    date: string
    time: string
    location: string
    meetingType: 'face-to-face' | 'virtual' | 'hybrid'
    meetingLink?: string
    duration?: number
    agenda?: string
    scheduledDateISO?: string
  }
  roleProfileConfirmed: boolean
  strategyCascadeReviewed: boolean
  objectivesDiscussed: boolean
  collaborationNotes?: string
  completedAt?: string
  createdAt: string
  scheduledBy: 'manager' | 'system'
}

interface ReviewsTabProps {
  employeeContext: EmployeeContext
  kpaData: KPAData[]
  evidence: EnhancedEvidenceEntry[]
  selectedYear: number
  userId: string | null
  employeeId?: string
  managerId?: string
  onLoadEvidence: (userId: string) => Promise<void>
  onRefetchPerformancePlan: () => Promise<void>
  
  // Collaborative sessions (merged from CollaborationTab)
  collaborativeSessions?: CollaborationSession[]
  isLoadingCollabSessions?: boolean
  
  // Evidence handlers (for review preparation workspace)
  onAddEvidence: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined>
  onUpdateEvidence: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onDeleteEvidence: (evidenceId: string) => Promise<void>
  onUploadDocument: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onUpdateEvidenceClassification: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => Promise<void>
}

/**
 * The events that belong in a calendar cell. The cell is a local date and
 * each event goes in the cell of its South African day. Check-in times come
 * from the API in UTC without a zone marker, so they are read as UTC.
 * (Keying cells by toISOString() put each cell on the day before in South
 * Africa, so check-ins showed a day late.)
 */
export function eventsOnCalendarDay<T extends { kind: string; date?: Date | null; raw?: any }>(
  events: T[],
  day: Date,
): T[] {
  const cell = format(day, 'yyyy-MM-dd')
  return events.filter(e => {
    const stamp: string | undefined = e.kind === 'review' ? (e.raw?.review_date || e.raw?.completed_date) : undefined
    if (stamp) return sastDay(stamp.endsWith('Z') ? stamp : stamp + 'Z') === cell
    return !!e.date && !Number.isNaN(e.date.getTime()) && sastToday(e.date) === cell
  })
}

export function ReviewsTab({
  employeeContext,
  kpaData,
  evidence,
  selectedYear,
  userId,
  employeeId,
  managerId,
  onLoadEvidence,
  onRefetchPerformancePlan,
  collaborativeSessions = [],
  isLoadingCollabSessions = false,
  onAddEvidence,
  onUpdateEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onUpdateEvidenceClassification
}: ReviewsTabProps) {
  // Shared Simple ↔ Detailed journey view mode (default 'simple' — calm,
  // judgement-free; 'detailed' restores the metrics dashboard + stronger pills).
  const [viewMode] = useJourneyViewMode()

  // View mode state for sessions section (list / monthly / calendar layouts)
  const [sessionViewMode, setSessionViewMode] = useState<'list' | 'monthly' | 'calendar'>('list')
  const [calendarDate, setCalendarDate] = useState(new Date())

  // Review cycle state
  const [reviewCycle, setReviewCycle] = useState<any[]>([])
  const [isLoadingReviews, setIsLoadingReviews] = useState(false)
  const [selectedReviewSession, setSelectedReviewSession] = useState<any>(null)
  const [viewReviewDialogOpen, setViewReviewDialogOpen] = useState(false)
  const [loadingReviewSession, setLoadingReviewSession] = useState(false)
  
  // Review preparation state
  const [showReviewPreparation, setShowReviewPreparation] = useState(false)
  const [selectedReviewForPrep, setSelectedReviewForPrep] = useState<'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc' | null>(null)
  const [selectedReviewDate, setSelectedReviewDate] = useState<Date | null>(null)
  const [reviewSessionId, setReviewSessionId] = useState<string | undefined>()
  
  // Manager ratings for employee review view
  const [managerRatings, setManagerRatings] = useState<Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>>({})
  const [managerComments, setManagerComments] = useState<Record<string, string>>({})
  const [managerOverallRating, setManagerOverallRating] = useState<'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | undefined>()

  /**
   * Load review cycle data
   */
  const loadReviewCycle = useCallback(async (targetUserId: string, year: number) => {
    if (!targetUserId || sessionManager.isExpired()) {
      if (!targetUserId) setReviewCycle([])
      return
    }

    setIsLoadingReviews(true)
    try {
      if (import.meta.env.DEV) console.log(`📅 Loading review cycle for user: ${targetUserId}, year: ${year}`)
      const reviews = await reviewService.getUserReviewCycle(targetUserId, year)
      
      // Sort by review_date
      const sortedReviews = reviews.sort((a, b) => {
        const reviewOrder: Record<string, number> = {
          'q1_checkin': 1,
          'midyear': 2,
          'q3_checkin': 3,
          'yearend': 4
        }
        return (reviewOrder[a.review_type] || 99) - (reviewOrder[b.review_type] || 99)
      })
      
      setReviewCycle(sortedReviews)
      if (import.meta.env.DEV) console.log(`✅ Loaded ${sortedReviews.length} review sessions`)
    } catch (error) {
      console.error('Failed to load review cycle:', error)
      setReviewCycle([])
    } finally {
      setIsLoadingReviews(false)
    }
  }, [])

  // Load review cycle on mount
  useEffect(() => {
    if (employeeId) {
      loadReviewCycle(employeeId, selectedYear)
    }
  }, [employeeId, selectedYear, loadReviewCycle])

  // Load manager ratings when employee opens review preparation
  useEffect(() => {
    const loadManagerRatings = async () => {
      if (!showReviewPreparation || !selectedReviewForPrep || !employeeId || sessionManager.isExpired()) return

      try {
        // First try to load IN_PROGRESS draft session (employee's preparation)
        const draftSessions = await reviewService.getUserReviewSessions(
          employeeId, 
          selectedReviewForPrep as any,
          0, // is_complete=0 (drafts)
          new Date().getFullYear()
        )
        
        if (draftSessions && draftSessions.length > 0) {
          // Use the most recent draft session
          const session = draftSessions[0]
          if (import.meta.env.DEV) console.log('📋 Loaded draft session for preparation:', session.id)
          setReviewSessionId(session.id)
          // Don't load manager ratings for drafts
          setManagerRatings({})
          setManagerComments({})
          setManagerOverallRating(undefined)
          return
        }
        
        // If no draft, try to load completed review session
        const completedSession = await reviewService.getLatestReviewSession(
          employeeId, 
          selectedReviewForPrep as any,
          new Date().getFullYear()
        )
        
        if (completedSession) {
          const { managerRatings: ratings, managerComments: comments, managerOverallRating: overall } = 
            reviewService.convertToWorkspaceFormat(completedSession)
          
          setManagerRatings(ratings)
          setManagerComments(comments)
          setManagerOverallRating(overall)
          setReviewSessionId(completedSession.id)
        } else {
          // No sessions at all
          setManagerRatings({})
          setManagerComments({})
          setManagerOverallRating(undefined)
          setReviewSessionId(undefined)
        }
      } catch (error) {
        console.error('Error loading review session:', error)
      }
    }

    loadManagerRatings()
  }, [showReviewPreparation, selectedReviewForPrep, employeeId])

  /**
   * View review details
   */
  const handleViewReviewDetails = async (reviewId: string) => {
    if (import.meta.env.DEV) console.log('View review details:', reviewId)
    setLoadingReviewSession(true)
    try {
      const session = await reviewService.getReviewSession(reviewId)
      
      // Convert KPA ratings to workspace format
      const ratings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'> = {}
      const comments: Record<string, string> = {}
      
      if (session.kpa_ratings) {
        session.kpa_ratings.forEach((kpaRating: any) => {
          const kpaKey = kpaRating.kpa_name || kpaRating.kpa_id
          
          if (kpaRating.rating) {
            ratings[kpaKey] = kpaRating.rating
          }
          if (kpaRating.manager_comments) {
            comments[kpaKey] = kpaRating.manager_comments
          }
        })
      }
      
      if (import.meta.env.DEV) console.log('📊 Converted ratings:', ratings)

      setManagerRatings(ratings)
      setManagerComments(comments)
      // Prefer the calibrated rating (post-calibration) over the initial.
      const finalRating = (session as any).calibrated_rating || session.initial_rating
      if (finalRating) {
        setManagerOverallRating(finalRating as 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr')
      }
      setSelectedReviewSession(session)
      setViewReviewDialogOpen(true)
    } catch (error) {
      console.error('Failed to load review session:', error)
      toast.error('Failed to load review details')
    } finally {
      setLoadingReviewSession(false)
    }
  }

  // ─── Unified Event Model ───────────────────────────────────────────
  // Merge review sessions and collaborative sessions into a single list

  type UnifiedEvent = {
    id: string
    kind: 'review' | 'collaboration'
    title: string
    subtitle: string
    date: Date | null          // sortable date
    dateLabel: string          // display string
    timeLabel: string | null
    status: 'completed' | 'scheduled' | 'in-progress' | 'pending' | 'overdue' | 'cancelled'
    statusLabel: string
    statusColor: string        // tailwind badge classes
    iconBg: string
    icon: React.ReactNode
    location?: string
    locationType?: 'virtual' | 'face-to-face' | 'hybrid'
    // review-specific
    reviewType?: string
    reviewId?: string
    isFormal?: boolean
    requiresRating?: boolean
    initiatedBy?: 'employee' | 'manager' | null
    canPrepare?: boolean
    canReschedule?: boolean
    canCancel?: boolean
    // collaboration-specific
    collabChecklist?: { roleProfile: boolean; strategy: boolean; objectives: boolean }
    // original data
    raw: any
  }

  // Review type formatting
  const formatReviewType = (rt: string) => {
    const m: Record<string, string> = {
      q1_checkin: 'Q1 Check-in', midyear: 'Mid-Year', q3_checkin: 'Q3 Check-in',
      yearend: 'Year-End', adhoc: 'On-Demand Check-in', probation: 'Probation Check-in',
      performance_improvement: 'Impact & Growth Support'
    }
    return m[rt] || rt
  }

  const mapReviewType = (rt: string): 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc' => {
    const m: Record<string, any> = { yearend: 'year-end', midyear: 'mid-year', q1_checkin: 'q1-checkin', q3_checkin: 'q3-checkin', adhoc: 'adhoc', probation: 'adhoc', performance_improvement: 'adhoc' }
    return m[rt] || 'adhoc'
  }

  // Normalise a status string to our enum
  const normaliseStatus = (s: string): UnifiedEvent['status'] => {
    if (s === 'completed') return 'completed'
    if (s === 'scheduled') return 'scheduled'
    if (s === 'in-progress' || s === 'in_progress') return 'in-progress'
    if (s === 'pending-scheduling' || s === 'pending_scheduling' || s === 'pending') return 'pending'
    if (s === 'overdue') return 'overdue'
    if (s === 'cancelled') return 'cancelled'
    return 'pending'
  }

  const statusMeta = (s: UnifiedEvent['status']): { label: string; color: string; iconBg: string; icon: React.ReactNode } => {
    switch (s) {
      case 'completed': return { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200', iconBg: 'bg-green-50', icon: <CheckCircle2 className="h-4 w-4 text-green-600" /> }
      case 'scheduled': return { label: 'Scheduled', color: 'bg-blue-100 text-blue-700 border-blue-200', iconBg: 'bg-blue-50', icon: <Calendar className="h-4 w-4 text-blue-600" /> }
      case 'in-progress': return { label: 'In Progress', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', iconBg: 'bg-emerald-50', icon: <Users className="h-4 w-4 text-emerald-600" /> }
      case 'pending': return { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', iconBg: 'bg-amber-50', icon: <Clock className="h-4 w-4 text-amber-600" /> }
      case 'overdue': return viewMode === 'detailed'
        ? { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-200', iconBg: 'bg-red-50', icon: <Clock className="h-4 w-4 text-red-600" /> }
        // Simple view: no RAG red on this employee surface — quiet stone tone,
        // framed as guidance ("past recommended date") rather than a failure.
        : { label: 'Past recommended date', color: 'bg-stone-100 text-stone-600 border-stone-200', iconBg: 'bg-stone-50', icon: <Clock className="h-4 w-4 text-stone-500" /> }
      case 'cancelled': return { label: 'Cancelled', color: 'bg-gray-100 text-gray-500 border-gray-200', iconBg: 'bg-gray-50', icon: <Clock className="h-4 w-4 text-gray-400" /> }
    }
  }

  // Check-in times come from the API in UTC without a zone marker; session
  // times carry one. Dates show the South African day.
  const formatDate = (d: string | null) => {
    if (!d) return 'Not Yet Scheduled'
    return sastCalendarDate(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  const formatTime = (d: string | null): string | null => {
    if (!d) return null
    const parsed = parseApiDate(d)
    // Collaboration sessions can carry a date-only value (e.g. "1 May 2026")
    // with no parseable time portion. Render the date alone rather than a
    // trailing ", Invalid Date".
    if (isNaN(parsed.getTime())) return null
    return parsed.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: true })
  }

  // Build unified events
  const unifiedEvents: UnifiedEvent[] = useMemo(() => {
    const events: UnifiedEvent[] = []

    // Reviews → events
    reviewCycle.forEach((r: any) => {
      const st = normaliseStatus(r.status)
      const meta = statusMeta(st)
      const isJIT = ['adhoc', 'probation', 'performance_improvement'].includes(r.review_type)
      events.push({
        id: `review-${r.id}`,
        kind: 'review',
        title: formatReviewType(r.review_type),
        subtitle: r.is_formal ? 'Formal 1:1 check-in' : (isJIT ? (r.initiated_by === 'employee' ? 'Requested by you' : 'Scheduled by people leader') : 'Check-in'),
        date: r.review_date ? parseApiDate(r.review_date) : (r.completed_date ? parseApiDate(r.completed_date) : null),
        dateLabel: formatDate(r.review_date),
        timeLabel: formatTime(r.review_date),
        status: st,
        statusLabel: meta.label,
        statusColor: meta.color,
        iconBg: meta.iconBg,
        icon: meta.icon,
        reviewType: r.review_type,
        reviewId: r.id,
        isFormal: r.is_formal,
        requiresRating: r.requires_rating,
        initiatedBy: r.initiated_by,
        canPrepare: st === 'scheduled' && !!r.review_date,
        canReschedule: st === 'scheduled' && isJIT,
        canCancel: st === 'scheduled' && isJIT && (r.initiated_by === 'employee' || r.initiated_by === null),
        raw: r
      })
    })

    // Collab sessions → events
    collaborativeSessions.forEach(c => {
      const st = normaliseStatus(c.status)
      const meta = statusMeta(st)
      const sessionDate = c.meetingDetails?.scheduledDateISO || c.meetingDetails?.date || c.completedAt || c.createdAt
      events.push({
        id: `collab-${c.id}`,
        kind: 'collaboration',
        title: 'Objective Setting Session',
        subtitle: '',
        date: sessionDate ? new Date(sessionDate) : null,
        dateLabel: sessionDate ? formatDate(sessionDate) : 'Pending scheduling',
        timeLabel: sessionDate ? formatTime(sessionDate) : null,
        status: st,
        statusLabel: meta.label,
        statusColor: meta.color,
        iconBg: meta.iconBg,
        icon: meta.icon,
        location: c.meetingDetails?.location,
        locationType: c.meetingDetails?.meetingType,
        collabChecklist: { roleProfile: c.roleProfileConfirmed, strategy: c.strategyCascadeReviewed, objectives: c.objectivesDiscussed },
        raw: c
      })
    })

    return events
  }, [reviewCycle, collaborativeSessions, viewMode])

  // ─── Filters ──────────────────────────────────────────────────────
  const [typeFilter, setTypeFilter] = useState<'all' | 'review' | 'collaboration'>('all')
  const [statusFilterVal, setStatusFilterVal] = useState<'all' | 'completed' | 'scheduled' | 'pending' | 'in-progress'>('all')

  const filteredEvents = useMemo(() => {
    let list = unifiedEvents
    if (typeFilter !== 'all') list = list.filter(e => e.kind === typeFilter)
    if (statusFilterVal !== 'all') list = list.filter(e => e.status === statusFilterVal)
    return list
  }, [unifiedEvents, typeFilter, statusFilterVal])

  // For list & monthly views, hide completed collab sessions (still visible in calendar)
  const listViewEvents = useMemo(() => {
    return filteredEvents.filter(e => !(e.kind === 'collaboration' && e.status === 'completed'))
  }, [filteredEvents])

  // Sort: active first (in-progress → scheduled → pending → overdue), then completed, then cancelled.
  // Within a status group: NOT-yet-completed items run earliest → latest (the soonest
  // upcoming check-in sits on top); completed/cancelled history stays newest-first.
  // Uses listViewEvents (excludes completed collab) for list & monthly; calendar uses filteredEvents directly
  const sortedEvents = useMemo(() => {
    const order: Record<string, number> = { 'in-progress': 0, 'scheduled': 1, 'overdue': 2, 'pending': 3, 'completed': 4, 'cancelled': 5 }
    return [...listViewEvents].sort((a, b) => {
      const oa = order[a.status] ?? 6
      const ob = order[b.status] ?? 6
      if (oa !== ob) return oa - ob
      const da = a.date?.getTime() ?? 0
      const db = b.date?.getTime() ?? 0
      // a.status === b.status here, so checking either is fine.
      const isHistory = a.status === 'completed' || a.status === 'cancelled'
      return isHistory ? db - da : da - db
    })
  }, [listViewEvents])

  // ─── Stats (global) ───────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = unifiedEvents.length
    const completed = unifiedEvents.filter(e => e.status === 'completed').length
    const scheduled = unifiedEvents.filter(e => e.status === 'scheduled').length
    const pending = unifiedEvents.filter(e => e.status === 'pending').length
    const inProgress = unifiedEvents.filter(e => e.status === 'in-progress').length
    return { total, completed, scheduled, pending, inProgress }
  }, [unifiedEvents])

  // ─── On-Demand presence ───────────────────────────────────────────
  // Does the user actually have any adhoc / on-demand check-ins? If not we
  // don't surface a dedicated On-Demand section — just a discreet, optional
  // "request a check-in" affordance so it never reads as required.
  const hasOnDemandCheckins = useMemo(
    () => reviewCycle.some((r: any) =>
      ['adhoc', 'probation', 'performance_improvement'].includes(r.review_type)
    ),
    [reviewCycle]
  )

  // ─── Calendar helpers ─────────────────────────────────────────────
  const navigateMonth = (dir: 'prev' | 'next') => {
    setCalendarDate(prev => {
      const d = new Date(prev)
      d.setMonth(d.getMonth() + (dir === 'prev' ? -1 : 1))
      return d
    })
  }
  const getDaysInMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  const getFirstDayOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1).getDay()
  const isToday = (d: Date) => { const t = new Date(); return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate() }

  const calendarDays = useMemo(() => {
    const days: (Date | null)[] = []
    for (let i = 0; i < getFirstDayOfMonth(calendarDate); i++) days.push(null)
    for (let i = 1; i <= getDaysInMonth(calendarDate); i++) days.push(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), i))
    return days
  }, [calendarDate])

  const getEventsForDate = (d: Date) => eventsOnCalendarDay(filteredEvents, d)

  // Group by month
  const eventsByMonth = useMemo(() => {
    const grouped: Record<string, UnifiedEvent[]> = {}
    sortedEvents.forEach(e => {
      const d = sastCalendarDate(e.date || new Date())
      const key = d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long' })
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(e)
    })
    // Earliest month first (chronological), so upcoming check-ins read top-to-bottom.
    return Object.entries(grouped).sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
  }, [sortedEvents])

  // ─── On-Demand Check-in & Reschedule dialogs (from EmployeeReviewManager) ──
  const [jitDialogOpen, setJitDialogOpen] = useState(false)
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false)
  const [selectedForReschedule, setSelectedForReschedule] = useState<any>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedForCancel, setSelectedForCancel] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)

  const handleCancelSession = async () => {
    if (!selectedForCancel) return
    try {
      setIsCancelling(true)
      await reviewService.cancelReviewSession(selectedForCancel, undefined, 'employee')
      toast.success('Check-in cancelled successfully')
      setCancelDialogOpen(false)
      setSelectedForCancel(null)
      loadReviewCycle(employeeId || '', selectedYear)
    } catch (error) {
      console.error('Error cancelling session:', error)
      toast.error('Failed to cancel check-in')
    } finally {
      setIsCancelling(false)
    }
  }

  // ─── Render a single unified event card ───────────────────────────
  // Matches the manager's TeamReviewManagement horizontal card layout
  const renderEventCard = (event: UnifiedEvent) => {
    const kindBadge = event.kind === 'review'
      ? 'bg-purple-100 text-purple-700 border-purple-200'
      : 'bg-orange-100 text-orange-700 border-orange-200'

    return (
      <div key={event.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white border rounded-lg hover:shadow-md transition-shadow">
        {/* Left: avatar + info */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${
            event.kind === 'review' ? 'bg-heritage-blue/10' : 'bg-orange-100'
          }`}>
            {event.kind === 'review'
              ? <Award className="h-5 w-5 text-heritage-blue" />
              : <Users className="h-5 w-5 text-orange-600" />}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-semibold text-gray-900">{event.title}</p>
              <Badge className={`${kindBadge} border`}>
                {event.kind === 'review' ? 'Check-In' : 'Collaboration'}
              </Badge>
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span className="text-xs text-muted-foreground">
                  {event.dateLabel}{event.timeLabel ? `, ${event.timeLabel}` : ''}
                </span>
              </div>
              {event.location && (
                <div className="flex items-center gap-1">
                  {event.locationType === 'virtual' ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                  <span className="text-xs text-muted-foreground">{event.location}</span>
                </div>
              )}
              {event.subtitle && (
                <span className="text-xs text-muted-foreground">{event.subtitle}</span>
              )}
            </div>

          </div>
        </div>

        {/* Right: status badge + action buttons */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:flex-shrink-0">
          <Badge className={`${event.statusColor} flex items-center gap-1`}>
            {event.icon}
            <span className="capitalize">{event.statusLabel}</span>
          </Badge>

          {/* Review actions */}
          {event.kind === 'review' && event.status === 'completed' && (
            <Button size="sm" variant="ghost" onClick={() => handleViewReviewDetails(event.reviewId!)}>
              View Summary
            </Button>
          )}
          {event.kind === 'review' && event.canPrepare && (
            <Button size="sm" className="bg-heritage-blue" onClick={() => { setReviewSessionId(undefined); setManagerRatings({}); setManagerComments({}); setManagerOverallRating(undefined); setSelectedReviewDate(event.date || null); setSelectedReviewForPrep(mapReviewType(event.reviewType!)); setShowReviewPreparation(true) }}>
              {event.isFormal ? 'Prepare for 1:1 Check-In' : 'Prepare for Check-in'}
            </Button>
          )}
          {event.kind === 'review' && event.status === 'scheduled' && event.canReschedule && (
            <Button size="sm" variant="outline" onClick={() => { setSelectedForReschedule(event.raw); setRescheduleDialogOpen(true) }}>
              Reschedule
            </Button>
          )}
          {event.kind === 'review' && event.canCancel && (
            <Button
              size="sm"
              variant="outline"
              className={viewMode === 'detailed'
                ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                : 'text-stone-600 hover:text-stone-700 hover:bg-stone-50'}
              onClick={() => { setSelectedForCancel(event.reviewId!); setCancelDialogOpen(true) }}
            >
              Cancel
            </Button>
          )}
          {event.kind === 'review' && event.status === 'pending' && (
            <Button size="sm" variant="outline" disabled>Not Yet Scheduled</Button>
          )}

          {/* Collab completed date */}
          {event.kind === 'collaboration' && event.status === 'completed' && event.raw?.completedAt && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {new Date(event.raw.completedAt).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
      </div>
    )
  }

  // ─── Loading state ────────────────────────────────────────────────
  const isLoading = isLoadingReviews || isLoadingCollabSessions

  return (
    <>
      <div className="space-y-6">
        {viewMode === 'detailed' ? (
          /* Detailed view — full statistics overview (matches manager
             TeamReviewManagement layout). Metrics live here, not on the
             default calm surface. */
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 pt-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600 font-medium">Total Sessions</p>
                  <p className="text-3xl font-bold text-heritage-blue mt-1">{stats.total}</p>
                  <p className="text-xs text-gray-500 mt-1">Check-ins & collaboration</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <p className="text-sm text-gray-600 font-medium">Completed</p>
                  </div>
                  <p className="text-3xl font-bold text-green-600 mt-1">{stats.completed}</p>
                  <p className="text-xs text-gray-500 mt-1">{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}% completion</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <p className="text-sm text-gray-600 font-medium">Scheduled</p>
                  </div>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{stats.scheduled}</p>
                  <p className="text-xs text-gray-500 mt-1">Ready to attend</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-emerald-600" />
                    <p className="text-sm text-gray-600 font-medium">In Progress</p>
                  </div>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">{stats.inProgress}</p>
                  <p className="text-xs text-gray-500 mt-1">Active sessions</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <p className="text-sm text-gray-600 font-medium">Pending</p>
                  </div>
                  <p className="text-3xl font-bold text-amber-600 mt-1">{stats.pending}</p>
                  <p className="text-xs text-gray-500 mt-1">Not scheduled</p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Simple view (default) — a calm editorial opener instead of a
             colored stat grid. Same journal voice as the sibling tabs. */
          <div className="border-b border-stone-200 pt-8 md:pt-12 pb-6 md:pb-8">
            {/* Centred 720px reading column — consistent with the Impact
                Portfolio / Objectives editorial headers. */}
            <div className="max-w-[720px] mx-auto">
              <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-3">
                Your conversations
              </p>
              {/* Page title intentionally omitted — the "my Check-Ins" nav tab
                  already names this surface. */}
              <p className="font-editorial italic text-quarterly-ink-muted text-[18px] md:text-[20px] leading-[1.55] max-w-[600px]">
                The conversations you have with your people leader along the way — moments to reflect, recalibrate, and grow. Take them at the pace that suits you.
              </p>
            </div>
          </div>
        )}

        {/* Filters and view controls - matches manager TeamReviewManagement layout */}
        <Card>
          <CardHeader>
            {/* Toolbar — view-mode switcher only. The "my check-ins" title was
                removed (the nav tab already names this surface). */}
            <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant={sessionViewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSessionViewMode('list')}
                  className={sessionViewMode === 'list' ? 'bg-heritage-blue' : ''}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  List
                </Button>
                <Button
                  variant={sessionViewMode === 'monthly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSessionViewMode('monthly')}
                  className={sessionViewMode === 'monthly' ? 'bg-heritage-blue' : ''}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Monthly
                </Button>
                <Button
                  variant={sessionViewMode === 'calendar' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSessionViewMode('calendar')}
                  className={sessionViewMode === 'calendar' ? 'bg-heritage-blue' : ''}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Calendar
                </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Filters row */}
              <div className="flex flex-col md:flex-row gap-3">
                <Select value={statusFilterVal} onValueChange={(v: any) => setStatusFilterVal(v)}>
                  <SelectTrigger className="w-full md:w-48">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={(v: any) => setTypeFilter(v)}>
                  <SelectTrigger className="w-full md:w-48">
                    <Award className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="review">Check-Ins Only</SelectItem>
                    <SelectItem value="collaboration">Collaboration Only</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex-1" />

                {/* On-Demand affordance.
                    #3: the On-Demand check-in is never required. When the user
                    already has ≥1 on-demand check-in we offer a normal "request
                    another" action; when they have none we show only a discreet,
                    judgement-free link so it never reads as an outstanding task.
                    Stronger (orange) styling is reserved for Detailed view. */}
                {hasOnDemandCheckins ? (
                  <Button
                    onClick={() => setJitDialogOpen(true)}
                    variant="outline"
                    className={viewMode === 'detailed'
                      ? 'border-orange-300 text-orange-700 hover:bg-orange-50'
                      : 'border-stone-300 text-stone-700 hover:bg-stone-50'}
                    disabled={!employeeId || !employeeContext.reportingManagerId}
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    request a check-in
                  </Button>
                ) : (
                  <Button
                    onClick={() => setJitDialogOpen(true)}
                    variant="ghost"
                    size="sm"
                    className="text-stone-500 hover:text-stone-700 hover:bg-stone-50 font-normal"
                    disabled={!employeeId || !employeeContext.reportingManagerId}
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    request a check-in
                  </Button>
                )}
              </div>

              {/* Content views */}
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-heritage-blue" />
                  <span className="ml-3 text-gray-600">Loading check-ins & sessions...</span>
                </div>
              ) : sortedEvents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">
                    {typeFilter !== 'all' || statusFilterVal !== 'all' ? 'No sessions match the current filters' : 'No check-ins found'}
                  </p>
                  <p className="text-sm">Contact your people leader to schedule check-ins or collaboration sessions</p>
                </div>
              ) : sessionViewMode === 'list' ? (
                /* ─── List View ─── */
                <div className="space-y-2">
                  {sortedEvents.map(renderEventCard)}
                </div>
              ) : sessionViewMode === 'monthly' ? (
                /* ─── Monthly View ─── */
                <div className="space-y-6">
                  {eventsByMonth.map(([month, events]) => (
                    <Card key={month}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Calendar className="h-5 w-5 text-stone-500" />
                            <CardTitle className="text-lg font-serif font-normal">{month}</CardTitle>
                          </div>
                          {/* #5: RAG-colored per-month counters are Detailed-only —
                              they read as a metrics strip on the calm surface. */}
                          {viewMode === 'detailed' && (
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <span>{events.filter(e => e.status === 'completed').length} completed</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-blue-600" />
                                <span>{events.filter(e => e.status === 'scheduled').length} scheduled</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-amber-600" />
                                <span>{events.filter(e => e.status === 'pending').length} pending</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {events.map(renderEventCard)}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                /* ─── Calendar View ─── */
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Button variant="ghost" size="sm" onClick={() => navigateMonth('prev')}><ChevronLeft className="h-4 w-4" /></Button>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{calendarDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}</h4>
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => setCalendarDate(new Date())}>Today</Button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigateMonth('next')}><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>
                    ))}
                    {calendarDays.map((day, i) => {
                      if (!day) return <div key={`empty-${i}`} className="h-24" />
                      const dayEvents = getEventsForDate(day)
                      return (
                        <div
                          key={day.toISOString()}
                          className={`h-24 border rounded-lg p-1 text-xs overflow-hidden ${isToday(day) ? 'border-heritage-blue bg-blue-50/50' : 'border-gray-200'}`}
                        >
                          <span className={`font-medium ${isToday(day) ? 'text-heritage-blue' : 'text-gray-700'}`}>{day.getDate()}</span>
                          <div className="mt-0.5 space-y-0.5">
                            {dayEvents.slice(0, 3).map(e => (
                              <div key={e.id} className={`px-1 py-0.5 rounded text-[10px] truncate ${e.statusColor}`}>
                                {e.kind === 'review' ? e.title : 'Collab'}
                              </div>
                            ))}
                            {dayEvents.length > 3 && <div className="text-[10px] text-gray-500 pl-1">+{dayEvents.length - 3} more</div>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {/* Calendar legend */}
                  <div className="flex items-center gap-4 mt-4 pt-3 border-t">
                    <span className="text-xs text-gray-500 flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-purple-100 border border-purple-200"></span> Check-In</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-200"></span> Collaboration</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Review Preparation Workspace - portaled to body to escape stacking contexts */}
      {showReviewPreparation && selectedReviewForPrep && createPortal(
        <ConductReviewWorkspace
          open={showReviewPreparation}
          onClose={() => {
            setShowReviewPreparation(false)
            setSelectedReviewForPrep(null)
          }}
          memberName="Me (Self-Preparation)"
          memberId={employeeId || "current-user-id"}
          managerId={managerId || ""} 
          reviewType={selectedReviewForPrep}
          scheduledDate={selectedReviewDate}
          userRole="employee"
          reviewSessionId={reviewSessionId}
          managerRatings={managerRatings}
          managerComments={managerComments}
          managerOverallRating={managerOverallRating}
          kpaData={kpaData}
          evidence={evidence}
          feedbackItems={[]}
          onAddEvidence={onAddEvidence}
          onUpdateEvidence={onUpdateEvidence}
          onDeleteEvidence={onDeleteEvidence}
          onUploadDocument={onUploadDocument}
          onUpdateEvidenceClassification={onUpdateEvidenceClassification}
          onRefreshEvidence={async () => {
            if (employeeId) {
              await onLoadEvidence(employeeId)
              await onRefetchPerformancePlan()
            }
          }}
        />,
        document.body
      )}

      {/* View Completed Review/Check-in - uses ConductReviewWorkspace in employee read-only mode */}
      {selectedReviewSession && viewReviewDialogOpen && createPortal(
        <ConductReviewWorkspace
          open={viewReviewDialogOpen}
          onClose={() => {
            setViewReviewDialogOpen(false)
            setSelectedReviewSession(null)
          }}
          memberName="Me"
          memberId={employeeId || "current-user-id"}
          managerId={managerId || ""}
          reviewType={
            selectedReviewSession.review_type === 'midyear' ? 'mid-year' :
            selectedReviewSession.review_type === 'yearend' ? 'year-end' :
            selectedReviewSession.review_type === 'q1_checkin' ? 'q1-checkin' :
            selectedReviewSession.review_type === 'q3_checkin' ? 'q3-checkin' :
            'adhoc'
          }
          scheduledDate={selectedReviewSession.review_date ? parseApiDate(selectedReviewSession.review_date) : null}
          userRole="employee"
          reviewSessionId={selectedReviewSession.id}
          managerRatings={managerRatings}
          managerComments={managerComments}
          managerOverallRating={managerOverallRating}
          kpaData={kpaData}
          evidence={evidence}
          feedbackItems={[]}
          onAddEvidence={onAddEvidence}
          onUpdateEvidence={onUpdateEvidence}
          onDeleteEvidence={onDeleteEvidence}
          onUploadDocument={onUploadDocument}
          onUpdateEvidenceClassification={onUpdateEvidenceClassification}
          onRefreshEvidence={async () => {
            if (employeeId) {
              await onLoadEvidence(employeeId)
              await onRefetchPerformancePlan()
            }
          }}
        />,
        document.body
      )}

      {/* On-Demand Check-in Request Dialog */}
      {employeeId && employeeContext.reportingManagerId && (
        <RequestJITCheckinDialog
          open={jitDialogOpen}
          onOpenChange={setJitDialogOpen}
          employeeId={employeeId}
          managerId={employeeContext.reportingManagerId}
          managerName={employeeContext.reportingManager}
          onSuccess={() => loadReviewCycle(employeeId, selectedYear)}
        />
      )}

      {/* Reschedule Session Dialog */}
      {selectedForReschedule && employeeId && employeeContext.reportingManagerId && (
        <RescheduleSessionDialog
          open={rescheduleDialogOpen}
          onOpenChange={setRescheduleDialogOpen}
          sessionId={selectedForReschedule.id}
          employeeId={employeeId}
          managerId={employeeContext.reportingManagerId}
          currentDate={selectedForReschedule.review_date}
          currentNotes={selectedForReschedule.meeting_notes}
          onSuccess={() => { loadReviewCycle(employeeId, selectedYear); setSelectedForReschedule(null) }}
          onRefused={() => { loadReviewCycle(employeeId, selectedYear); setSelectedForReschedule(null) }}
        />
      )}

      {/* Cancel Check-in Confirmation Dialog */}
      {cancelDialogOpen && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel Check-in?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will cancel the check-in and remove it from your calendar. Your people leader will be notified of the cancellation.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setSelectedForCancel(null) }} disabled={isCancelling}>
                Keep Check-in
              </Button>
              <Button
                variant={viewMode === 'detailed' ? 'destructive' : 'default'}
                onClick={handleCancelSession}
                disabled={isCancelling}
                className={viewMode === 'detailed'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-stone-800 hover:bg-stone-900 text-white'}
              >
                {isCancelling ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cancelling...</>) : 'Yes, Cancel'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default ReviewsTab
